import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  TableActionTd,
  TableActionTh,
  TableBody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
} from '@widgets/table';
import Paginator, { DEFAULT_PER_PAGE } from '@widgets/paginator';
import {
  ILeftAccountList,
  useBoeingLeftAccountList,
} from '@app/queries/boeing/useBoeingLeftAccountList';
import {
  useFilters,
  useLimit,
  useListParamsAndPersistToUrl,
  usePage,
  useSort,
} from '@app/hooks/useList';
import { useUrlParams } from '@app/hooks/useUrlParams';
import { IAccountList } from '@app/queries/account/useAccountList';
import { setBreadcrumbs } from '@app/redux/features/breadcrumbsSlice';
import { BreadCrumbIcon, BreadCrumbType } from '@entities/util/IBreadCrumb';
import Section from '@widgets/components/section';
import AccountFilter, { IAccountFilter } from '@pages/Account/List/filter';
import { GrayButton } from '@shared/uikit/button';
import IconDownload from '@shared/icons/IconDownload';
import PermissionGuard from '@shared/security/PermissionGuard';
import { ACCOUNT_PERMISSIONS } from '@app/contstants/permissions';
import Search from '@widgets/components/search';
import DropdownButtonWithPermissions from '@widgets/dropdown-button-with-permissions';
import IconArrowUp from '@shared/icons/IconArrowUp';
import ValueWithSign from '@widgets/table/components/value-with-sign';
import IconCheck from '@shared/icons/IconCheck';
import IconDecline from '@shared/icons/IconDecline';
import IconVerticalThreeDots from '@shared/icons/IconVerticalThreeDots';
import EmptyList from '@widgets/empty-list';
import { ListLoader } from '@shared/uikit/loader';
import { CreateAccountPopup } from '@pages/Account/Create';
import { BulkAssignClientPopup } from '@pages/Account/List/BulkAssignClient';
import AccountPopupEdit from '@pages/Account/Edit';
import { DeletePrompt } from '@widgets/prompt';
import LeftHashRateFilter, {
  ILeftHashRateFilter,
} from '@pages/Main/components/LeftHashRate/filter';
import DropdownButton from '@widgets/dropdown-button';
import { useBoeingNewAccountList } from '@app/queries/boeing/useBoeingNewAccountList';

const LeftHashRateShow = () => {
  const dispatch = useDispatch();

  const boeingLeftAccountListFetcher = useBoeingLeftAccountList();

  const { urlParams } = useUrlParams();
  const page = usePage(urlParams, 1);
  const perPage = useLimit(urlParams, DEFAULT_PER_PAGE);
  const filtering = useFilters(urlParams, []);
  const sorting = useSort(urlParams);
  useListParamsAndPersistToUrl(page, perPage, filtering, sorting);

  const [bulkItems, setBulkItems] = useState<number[]>([]);
  const [list, setList] = useState<ILeftAccountList | null>(null);

  useEffect(() => {
    dispatch(
      setBreadcrumbs([
        {
          title: 'Sales DB',
          type: BreadCrumbType.LINK,
          link: '/main',
          icon: BreadCrumbIcon.VIA_BTC,
        },
        {
          title: 'Основные показатели',
          type: BreadCrumbType.LINK,
          link: '/main',
        },
        { title: 'Отток хешрейта', type: BreadCrumbType.TEXT },
      ])
    );
  }, []);

  useEffect(() => {
    (async () => {
      if (null === filtering.value) return;

      setList(null);
      const data = await boeingLeftAccountListFetcher.fetch({
        ...filtering.value,
        page: page.value ?? 1,
        sort: sorting.value,
        perPage: perPage.value ?? DEFAULT_PER_PAGE,
      });
      setList(data);
    })();
  }, [page.value, perPage.value, filtering.value, sorting.value?.column, sorting.value?.type]);

  const handlePageClick = useCallback(
    (selectedItem: { selected: number }) => page.setValue(selectedItem.selected + 1),
    []
  );

  const onChangePerPage = useCallback(
    (count: number) => {
      perPage.setValue(count);
      setBulkItems([]);
    },
    [perPage.value]
  );

  return (
    <>
      <Section>
        <div className="flex justify-between items-center w-full mb-[20px]">
          <LeftHashRateFilter
            filters={filtering.value}
            onChangeFilters={(values: ILeftHashRateFilter) => filtering.setValue(values)}
            onReset={() => {
              // const parsedReportLastSyncDate = new Date(reportLastSyncDate?.split('.')?.reverse());

              page.setValue(1);
              perPage.setValue(DEFAULT_PER_PAGE);
              sorting.setValue(null);
              filtering.setValue([]);
            }}
          />
          <div className="flex gap-[10px] pl-[10px] self-start">
            <div className="flex gap-[10px]">
              <GrayButton
                onClick={() => () => {}}
                title={
                  <div className="flex gap-[10px] items-center fill-[white]">
                    <IconDownload className="w-[11px] h-[14px] fill-[#D9D9D9]" />
                  </div>
                }
              />
            </div>
          </div>
        </div>

        {boeingLeftAccountListFetcher.isSuccess && list !== null ? (
          <>
            {list.items.length ? (
              <>
                <Table>
                  <TableThead>
                    <TableTr>
                      <TableActionTh
                        className="w-[3%]"
                        onClick={() => {
                          if (list.items.length === bulkItems.length) {
                            setBulkItems([]);
                          } else {
                            setBulkItems(list.items.map(item => item.client_id));
                          }
                        }}
                      />
                      <TableTh
                        title="Клиент"
                        className="w-[17%]"
                        sortName="client_name"
                        sort={sorting.value}
                        onSort={sorting.setValue}
                      />
                      <TableTh
                        title="Аккаунт"
                        className="w-[15%]"
                        sortName="account"
                        sort={sorting.value}
                        onSort={sorting.setValue}
                      />
                      <TableTh
                        title="Максимальный хешрейт"
                        className="w-[10%]"
                        sortName="max_hashrate"
                        sort={sorting.value}
                        onSort={sorting.setValue}
                      />
                      <TableTh
                        title="Текущий хешрейт"
                        className="w-[10%]"
                        sortName="hashrate"
                        sort={sorting.value}
                        onSort={sorting.setValue}
                      />
                      <TableTh
                        title="% падения"
                        className="w-[10%]"
                        sortName="percent_change"
                        sort={sorting.value}
                        onSort={sorting.setValue}
                      />
                      <TableTh
                        title="Менеджер"
                        className="w-[15%]"
                        sortName="manager"
                        sort={sorting.value}
                        onSort={sorting.setValue}
                      />
                      <TableTh
                        title="Причина падения"
                        className="w-[20%]"
                        sortName="reason"
                        sort={sorting.value}
                        onSort={sorting.setValue}
                      />
                    </TableTr>
                  </TableThead>
                  <TableBody>
                    {list &&
                      list.items.map((item, key) => (
                        <TableTr key={key}>
                          <TableActionTd
                            items={bulkItems}
                            value={item.client_id}
                            onChange={setBulkItems}
                          />
                          <TableTd
                            title={
                              <ValueWithSign
                                value={item.client_name ?? 'Нет клиента'}
                                color="bg-[#4598E6]"
                              />
                            }
                          />
                          <TableTd
                            title={
                              <div>
                                <ValueWithSign value={item.account} color="bg-[#99840F]" />
                              </div>
                            }
                          />
                          <TableTd title={item.max_hashrate + ' PH'} />
                          <TableTd title={item.hashrate + ' PH'} />
                          <TableTd title={item.percent_change + ' %'} />
                          <TableTd title={item.manager_name} />
                          <TableTd title={`${item.reason ? item.reason : ''}`} />
                        </TableTr>
                      ))}
                  </TableBody>
                </Table>

                <div className="pt-[30px] flex justify-end items-center w-full ">
                  <Paginator
                    totalPages={list.total_pages}
                    page={page.value ? page.value - 1 : 0}
                    perPage={perPage.value ?? DEFAULT_PER_PAGE}
                    onChangePerPage={onChangePerPage}
                    onChangePage={handlePageClick}
                    totalItems={`${list?.total_items} аккаунтов`}
                  />
                </div>
              </>
            ) : (
              <EmptyList />
            )}
          </>
        ) : (
          <ListLoader loading={true} />
        )}
      </Section>
    </>
  );
};

export default LeftHashRateShow;
